import type { GraphQLFormattedError } from 'graphql'

import { isAuthSessionGone, logOut } from './auth'

// A user whose token the backend keeps rejecting sees an app that looks logged in
// but every guarded query comes back empty. This logs them out instead.
//
// A single UNAUTHENTICATED is not enough on its own: the backend also answers it
// while the user service is briefly unreachable, and that must not sign anyone out.
// So we log out right away only when the sign-in session itself is gone, and
// otherwise only after the rejections keep coming across separate time windows.

const STRIKE_WINDOW_MS = 15_000
const STRIKES_TO_LOG_OUT = 3
const STRIKE_RESET_MS = 120_000

interface UnauthenticatedDeps {
  isSessionGone: () => Promise<boolean>
  logOut: () => Promise<unknown>
  redirect: () => void
  now: () => number
}

const defaultDeps: UnauthenticatedDeps = {
  isSessionGone: isAuthSessionGone,
  logOut,
  redirect: () => window.location.replace('/'),
  now: () => Date.now(),
}

export const hasUnauthenticatedError = (errors?: ReadonlyArray<GraphQLFormattedError>): boolean =>
  errors?.some((error) => error.extensions?.code === 'UNAUTHENTICATED') ?? false

export const createUnauthenticatedHandler = (deps: UnauthenticatedDeps = defaultDeps) => {
  let strikes = 0
  let lastStrikeAt = 0
  let loggingOut = false
  let checking: Promise<void> | null = null

  const signOut = async (): Promise<void> => {
    loggingOut = true
    await deps.logOut()
    deps.redirect()
  }

  const check = async (): Promise<void> => {
    const now = deps.now()
    if (now - lastStrikeAt > STRIKE_RESET_MS) strikes = 0
    // Many queries fail together after one bad token; count them as one strike.
    if (strikes > 0 && now - lastStrikeAt < STRIKE_WINDOW_MS) return
    strikes += 1
    lastStrikeAt = now

    if (await deps.isSessionGone()) return signOut()
    if (strikes >= STRIKES_TO_LOG_OUT) return signOut()
  }

  return (): Promise<void> => {
    // Visitors who never signed in (guests, incognito) get these errors by design.
    if (loggingOut || !localStorage.getItem('auth_type') || sessionStorage.getItem('incognitoMode')) {
      return Promise.resolve()
    }
    if (!checking) {
      checking = check().finally(() => {
        checking = null
      })
    }
    return checking
  }
}

export const handleUnauthenticated = createUnauthenticatedHandler()
